import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Type, Image as ImageIcon, Square, Plus, Move, Trash2, Copy, Smartphone } from 'lucide-react';

/**
 * Fluid Canvas Block — Squarespace 7.1 Fluid Engine clone.
 *
 * The section is a 24-column × N-row grid. Each child element has a
 * gridArea: { col, row, w, h }. Children render as absolutely-positioned
 * <div>s inside the relative grid container.
 *
 * MOBILE-RESPONSIVE: each child can optionally carry a `mobile` override
 * `{ col, row, w, h }` used only when the preview device is `phone` (or
 * the actual viewport is < 768px). Editing in phone mode writes ONLY to
 * the mobile override — desktop layout is preserved. Editing in desktop
 * mode writes to the top-level fields. This means you can fine-tune the
 * phone view without messing up the desktop view (and vice versa).
 *
 * Users can:
 *   - Drag any child to reposition (mouse moves snap to grid cells)
 *   - Drag the bottom-right corner to resize
 *   - Click + Add Element to insert text / image / button
 *   - Click an element to edit text inline
 */

const COLS = 24;
const ROW_HEIGHT_PX = 32; // each grid row = 32px
const DEFAULT_ROWS = 14;

const DEFAULT_CHILDREN = [
  { id: 'heading', type: 'heading', text: 'Drag-and-drop headline', col: 4, row: 2, w: 16, h: 3 },
  { id: 'paragraph', type: 'paragraph', text: 'Drop blocks anywhere on the grid. Snap to 24 columns, resize from the corner.', col: 4, row: 6, w: 16, h: 2 },
  { id: 'button', type: 'button', text: 'Get Started', col: 4, row: 9, w: 5, h: 1 },
];

export const newFluidChild = (type) => {
  const presets = {
    heading: { type: 'heading', text: 'Headline', col: 4, row: 1, w: 16, h: 2 },
    paragraph: { type: 'paragraph', text: 'New paragraph', col: 4, row: 3, w: 16, h: 2 },
    button: { type: 'button', text: 'Click me', col: 4, row: 5, w: 5, h: 1 },
    image: { type: 'image', src: '', col: 4, row: 1, w: 8, h: 6 },
    spacer: { type: 'spacer', col: 4, row: 1, w: 4, h: 2 },
  };
  return { id: `${type}-${Date.now()}`, ...(presets[type] || presets.paragraph) };
};

export const FLUID_DEFAULT_CONTENT = {
  rows: DEFAULT_ROWS,
  bg_color: '#0E0E23',
  fg_color: '#FFFFFF',
  children: DEFAULT_CHILDREN,
};

/**
 * Rendering / editing component used inside the website builder canvas.
 * `editable` toggles drag/resize handles + grid overlay.
 */
export default function FluidCanvas({ block, content, onUpdateChild, onAddChild, onRemoveChild, editable = true, device = 'desktop' }) {
  const c = content || block?.content || {};
  const rows = c.rows || DEFAULT_ROWS;
  const bg = c.bg_color || '#0E0E23';
  const fg = c.fg_color || '#FFFFFF';
  const children = c.children || [];

  // Mobile viewport detection — auto-fall-back if device prop isn't set.
  const [viewportIsMobile, setViewportIsMobile] = useState(false);
  // Editor preview-mode (desktop / mobile toggle in the toolbar). Read from
  // window.__wb_view_mode__ stashed by DashboardContent so we can toggle
  // mobile editing inside the live canvas without requiring an actual
  // narrow viewport (the editor itself is gated to desktop-only widths).
  const [editorViewMode, setEditorViewMode] = useState(
    typeof window !== 'undefined' ? (window.__wb_view_mode__ || 'desktop') : 'desktop'
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    setViewportIsMobile(mq.matches);
    const onChange = (e) => setViewportIsMobile(e.matches);
    const onModeChange = (e) => {
      // Accept both signal formats: detail = 'mobile' (string) or
      // detail = { mode: 'mobile' } (object). The DashboardContent dispatch
      // uses the string form, but external/devtools triggers may use either.
      const d = e?.detail;
      const mode = typeof d === 'string' ? d : (d?.mode || window.__wb_view_mode__ || 'desktop');
      setEditorViewMode(mode);
    };
    mq.addEventListener('change', onChange);
    window.addEventListener('wb:view-mode-change', onModeChange);
    return () => {
      mq.removeEventListener('change', onChange);
      window.removeEventListener('wb:view-mode-change', onModeChange);
    };
  }, []);
  const isMobile = device === 'phone' || editorViewMode === 'mobile' || (device === 'desktop' ? false : viewportIsMobile);

  // Effective layout for a child = mobile override if available + isMobile, else desktop.
  const effectiveLayout = (child) => {
    if (isMobile && child.mobile) {
      return {
        col: child.mobile.col ?? child.col,
        row: child.mobile.row ?? child.row,
        w: child.mobile.w ?? child.w,
        h: child.mobile.h ?? child.h,
      };
    }
    return { col: child.col, row: child.row, w: child.w, h: child.h };
  };

  const containerRef = useRef(null);
  const [dragState, setDragState] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [showAddMenu, setShowAddMenu] = useState(false);

  const onPointerDown = useCallback((e, child, mode) => {
    if (!editable) return;
    e.preventDefault();
    e.stopPropagation();
    setActiveId(child.id);
    const eff = effectiveLayout(child);
    setDragState({
      id: child.id, mode,
      startX: e.clientX, startY: e.clientY,
      startCol: eff.col, startRow: eff.row,
      startW: eff.w, startH: eff.h,
    });
  }, [editable, isMobile]);

  const onPointerMove = useCallback((e) => {
    if (!dragState || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const colPx = rect.width / COLS;
    const dCol = Math.round((e.clientX - dragState.startX) / colPx);
    const dRow = Math.round((e.clientY - dragState.startY) / ROW_HEIGHT_PX);

    const child = children.find(ch => ch.id === dragState.id);
    if (!child) return;
    const eff = effectiveLayout(child);

    // When editing in mobile mode, write to `mobile` override only so
    // desktop layout is preserved (and vice versa).
    const writeLayout = (patch) => {
      if (isMobile) {
        const nextMobile = { ...(child.mobile || {}), ...patch };
        onUpdateChild(dragState.id, { mobile: nextMobile });
      } else {
        onUpdateChild(dragState.id, patch);
      }
    };

    if (dragState.mode === 'move') {
      const nextCol = Math.max(0, Math.min(COLS - eff.w, dragState.startCol + dCol));
      const nextRow = Math.max(0, Math.min(rows - eff.h, dragState.startRow + dRow));
      writeLayout({ col: nextCol, row: nextRow });
    } else if (dragState.mode === 'resize') {
      const nextW = Math.max(1, Math.min(COLS - eff.col, dragState.startW + dCol));
      const nextH = Math.max(1, Math.min(rows - eff.row, dragState.startH + dRow));
      writeLayout({ w: nextW, h: nextH });
    }
  }, [dragState, children, onUpdateChild, rows, isMobile]);

  const onPointerUp = useCallback(() => setDragState(null), []);

  const handleAddChild = (type) => {
    onAddChild(newFluidChild(type));
    setShowAddMenu(false);
  };

  const handleTextEdit = (childId, newText) => {
    onUpdateChild(childId, { text: newText });
  };

  return (
    <div
      ref={containerRef}
      onPointerMove={dragState ? onPointerMove : undefined}
      onPointerUp={onPointerUp}
      onPointerLeave={onPointerUp}
      className="relative overflow-hidden select-none"
      style={{
        background: bg,
        color: fg,
        height: rows * ROW_HEIGHT_PX,
        minHeight: 280,
      }}
      data-testid={`fluid-canvas-${block?.id || 'preview'}`}
    >
      {/* GRID OVERLAY (only visible while editing/dragging) */}
      {editable && (
        <div className={`absolute inset-0 pointer-events-none transition-opacity duration-200 ${dragState ? 'opacity-100' : 'opacity-0 group-hover/section:opacity-40'}`}>
          <div className="absolute inset-0" style={{
            backgroundImage: `linear-gradient(rgba(167,139,250,0.18) 1px, transparent 1px), linear-gradient(90deg, rgba(167,139,250,0.18) 1px, transparent 1px)`,
            backgroundSize: `${100 / COLS}% ${ROW_HEIGHT_PX}px`,
          }} />
        </div>
      )}

      {/* CHILD BLOCKS */}
      {children.map((ch) => {
        const eff = effectiveLayout(ch);
        const left = (eff.col / COLS) * 100;
        const width = (eff.w / COLS) * 100;
        const top = eff.row * ROW_HEIGHT_PX;
        const height = eff.h * ROW_HEIGHT_PX;
        const isActive = activeId === ch.id;
        const hasMobileOverride = !!(ch.mobile && Object.keys(ch.mobile).length > 0);

        return (
          <div
            key={ch.id}
            className={`absolute group/fluidchild transition-shadow ${isActive ? 'z-30 shadow-[0_0_0_2px_rgba(167,139,250,0.6)]' : 'z-10 hover:shadow-[0_0_0_1px_rgba(167,139,250,0.3)]'}`}
            style={{ left: `${left}%`, top, width: `${width}%`, height }}
            onClick={(e) => { e.stopPropagation(); setActiveId(ch.id); }}
            data-testid={`fluid-child-${ch.id}`}
          >
            {/* Move handle (top-left) */}
            {editable && (
              <div
                onPointerDown={(e) => onPointerDown(e, ch, 'move')}
                className="absolute -top-2 -left-2 z-40 w-6 h-6 rounded-md bg-[#A78BFA] text-[#0E0E23] flex items-center justify-center opacity-0 group-hover/fluidchild:opacity-100 cursor-grab active:cursor-grabbing transition-opacity"
                title="Drag"
                data-testid={`fluid-move-${ch.id}`}
              >
                <Move size={11} />
              </div>
            )}
            {/* Resize handle (bottom-right) */}
            {editable && (
              <div
                onPointerDown={(e) => onPointerDown(e, ch, 'resize')}
                className="absolute -bottom-1 -right-1 z-40 w-3.5 h-3.5 rounded-sm bg-[#A78BFA] cursor-nwse-resize opacity-0 group-hover/fluidchild:opacity-100 transition-opacity"
                title="Resize"
                data-testid={`fluid-resize-${ch.id}`}
              />
            )}
            {/* Delete (top-right) */}
            {editable && isActive && (
              <button
                onClick={(e) => { e.stopPropagation(); onRemoveChild(ch.id); setActiveId(null); }}
                className="absolute -top-2 -right-2 z-40 w-6 h-6 rounded-md bg-[#FF6B6B] text-white flex items-center justify-center hover:bg-[#FF4444]"
                data-testid={`fluid-delete-${ch.id}`}
                title="Remove"
              >
                <Trash2 size={11} />
              </button>
            )}

            {/* Mobile-override indicator + reset action */}
            {editable && isActive && hasMobileOverride && (
              <button
                onClick={(e) => { e.stopPropagation(); onUpdateChild(ch.id, { mobile: null }); }}
                className="absolute -top-2 left-6 z-40 px-1.5 py-0.5 rounded-md bg-[#3B82F6] text-white text-[9px] uppercase tracking-wider font-bold flex items-center gap-1 hover:bg-[#2563EB]"
                data-testid={`fluid-reset-mobile-${ch.id}`}
                title="This element has a mobile-only position. Click to reset to desktop layout."
              >
                <Smartphone size={9} /> Mobile · Reset
              </button>
            )}

            {/* CONTENT */}
            <ChildContent child={ch} onTextEdit={(t) => handleTextEdit(ch.id, t)} fg={fg} editable={editable} />
          </div>
        );
      })}

      {/* ADD ELEMENT BUTTON */}
      {editable && (
        <div className="absolute bottom-3 left-3 z-50">
          <button
            onClick={() => setShowAddMenu(!showAddMenu)}
            className="px-3 py-1.5 bg-[#15152E]/90 border border-white/[0.12] text-white text-[11px] font-bold uppercase tracking-wider rounded-full hover:bg-[#A78BFA] hover:text-[#0E0E23] transition-all flex items-center gap-1.5 backdrop-blur-sm"
            data-testid="fluid-add-element"
          >
            <Plus size={11} /> Element
          </button>
          {showAddMenu && (
            <div className="absolute bottom-full mb-2 left-0 w-44 bg-[#15152E] border border-white/[0.12] rounded-lg shadow-[0_8px_24px_rgba(0,0,0,0.5)] p-1 z-50" data-testid="fluid-add-menu">
              {[
                { type: 'heading', label: 'Heading', icon: Type },
                { type: 'paragraph', label: 'Paragraph', icon: Type },
                { type: 'button', label: 'Button', icon: Square },
                { type: 'image', label: 'Image', icon: ImageIcon },
                { type: 'spacer', label: 'Spacer', icon: Square },
              ].map(({ type, label, icon: Ic }) => (
                <button
                  key={type}
                  onClick={() => handleAddChild(type)}
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-[12px] text-white hover:bg-white/[0.08] transition-all text-left"
                  data-testid={`fluid-add-${type}`}
                >
                  <Ic size={12} className="text-[#A78BFA]" /> {label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Status pill */}
      {editable && (
        <div
          className="absolute bottom-3 right-3 z-50 px-2.5 py-1 rounded-full text-[9.5px] uppercase tracking-[0.18em] font-bold bg-[#A78BFA]/15 text-[#A78BFA] border border-[#A78BFA]/30 flex items-center gap-1.5"
          data-testid={`fluid-status-pill-${block?.id || 'preview'}`}
          data-fluid-mode={isMobile ? 'mobile' : 'desktop'}
        >
          {isMobile && <Smartphone size={10} />}
          Fluid · {isMobile ? 'Mobile layout' : 'Desktop'} · {COLS}×{rows}
        </div>
      )}
    </div>
  );
}

function ChildContent({ child, onTextEdit, fg, editable }) {
  if (child.type === 'image') {
    return (
      <div className="w-full h-full rounded-md overflow-hidden bg-white/[0.05] flex items-center justify-center">
        {child.src ? (
          <img src={child.src} alt="" className="w-full h-full object-cover" />
        ) : (
          <ImageIcon size={28} className="text-white/30" />
        )}
      </div>
    );
  }
  if (child.type === 'button') {
    return (
      <button
        className="w-full h-full rounded-full bg-white text-[#0E0E23] font-bold text-[12px] uppercase tracking-wider hover:opacity-90 transition-opacity flex items-center justify-center px-2"
        onClick={(e) => e.preventDefault()}
      >
        <span
          contentEditable={editable}
          suppressContentEditableWarning
          onBlur={(e) => onTextEdit(e.currentTarget.textContent)}
          onClick={(e) => e.stopPropagation()}
          className="outline-none"
        >
          {child.text}
        </span>
      </button>
    );
  }
  if (child.type === 'spacer') {
    return <div className="w-full h-full" />;
  }
  const isHeading = child.type === 'heading';
  return (
    <div
      contentEditable={editable}
      suppressContentEditableWarning
      onBlur={(e) => onTextEdit(e.currentTarget.textContent)}
      onClick={(e) => e.stopPropagation()}
      className={`w-full h-full outline-none rounded-md p-1 ${isHeading ? 'text-3xl md:text-4xl font-bold font-syne tracking-tight' : 'text-base leading-relaxed'}`}
      style={{ color: fg }}
    >
      {child.text}
    </div>
  );
}
